import React, { FC, memo, useMemo } from "react";

// types
import { BlackMembershipWidgetProps } from "@swiggy-private/cart-types";

// hooks
import { SessionVisibility, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";

// components
import FtuBottomsheet from "./Components/FtuBottomsheet/FtuBottomsheet";
import { ErrorBoundary } from "@swiggy-private/core-ui";
import BlackPlanCrouton from "./Components/BlackPlanCrouton/BlackPlanCrouton";

// constants
import {
    BLACK_MEMBERSHIP_CROUTON_SID,
    FTU_BOTTOMSHEET_FREQUENCY_CAP_KEY,
} from "@super-ui/components/black-membership-widgets/constants";

// utils
import { isEmpty } from "@swiggy-private/js-utils";

const BlackMembershipWidgets: FC<BlackMembershipWidgetProps> = ({
    reinforcementComms,
    croutonComms,
    analyticsEvents,
}) => {
    const frequencyCap = reinforcementComms?.frequency_cap;

    const frequencyCapPayload = {
        key: FTU_BOTTOMSHEET_FREQUENCY_CAP_KEY,
        id: frequencyCap?.id,
        maxFrequency: Number(frequencyCap?.count),
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
    };

    const { shouldProceed, isSessionValid } = useLocalStorageBasedFrequencyCapping(frequencyCapPayload);

    const shouldShowFtuBottomsheet = useMemo(() => shouldProceed(), [shouldProceed]);
    const shouldBlackCroutonMount = useMemo(
        () => isSessionValid(BLACK_MEMBERSHIP_CROUTON_SID) && !shouldShowFtuBottomsheet,
        [isSessionValid, shouldShowFtuBottomsheet],
    );

    const ftueAnalytics = useMemo(
        () => ({
            onImpressionFtueHalfcard: analyticsEvents.onImpressionFtueHalfcard,
            onClickFtueCta: analyticsEvents.onClickFtueCta,
            onClickFtueClose: analyticsEvents.onClickFtueClose,
        }),
        [analyticsEvents.onClickFtueClose, analyticsEvents.onClickFtueCta, analyticsEvents.onImpressionFtueHalfcard],
    );

    const croutonAnalytics = useMemo(
        () => ({
            onImpressionCrouton: analyticsEvents.onImpressionCrouton,
            onClickCroutonClose: analyticsEvents.onClickCroutonClose,
        }),
        [analyticsEvents.onClickCroutonClose, analyticsEvents.onImpressionCrouton],
    );

    return (
        <ErrorBoundary>
            {!isEmpty(reinforcementComms) && shouldShowFtuBottomsheet && (
                <FtuBottomsheet {...reinforcementComms} analyticsEvents={ftueAnalytics} />
            )}

            {!isEmpty(croutonComms) && shouldBlackCroutonMount && (
                <BlackPlanCrouton {...croutonComms} analyticsEvents={croutonAnalytics} />
            )}
        </ErrorBoundary>
    );
};

export default memo(BlackMembershipWidgets);
